/**
 * Inline cup add-ons for the cart drawer.
 *
 * The production-time and lids selectors live inside each cup card and apply
 * live on change (no button). The matching rush-fee and lid cart lines are
 * added/replaced/removed automatically and fold into the cup card's combined
 * price. Removing a cup removes its linked add-ons too.
 */
(function () {
  const RUSH_VARIANT_ID = '50675778126106';
  const PRODUCTION_FEE_PERCENTAGES = Object.freeze({
    10: 0,
    7: 15,
    5: 30,
    4: 40,
    3: 50,
    2: 60,
  });
  const BOWL_LID_VARIANT_MAPPING = Object.freeze({
    'Flat White': {
      10000: '51751813742874',
      16000: '51751813841178',
      32000: '51751813939482',
      50000: '51751814037786',
      64000: '51751814136090',
    },
    'Flat Black': {
      10000: '51751813775642',
      16000: '51751813873946',
      32000: '51751813972250',
      50000: '51751814070554',
      64000: '51751814168858',
    },
    'Flat Clear': {
      10000: '51751813808410',
      16000: '51751813906714',
      32000: '51751814005018',
      50000: '51751814103322',
      64000: '51751814201626',
    },
    'Dome Clear (No Hole)': {
      10000: '51751813447962',
      16000: '51751813513498',
      32000: '51751813579034',
      50000: '51751813644570',
      64000: '51751813710106',
    },
    'Dome Clear (With Hole)': {
      10000: '51810695872794',
      16000: '51810695905562',
      32000: '51810695938330',
      50000: '51810695971098',
      64000: '51810696003866',
    },
    'High Dome Clear': {
      10000: '51751814299930',
      16000: '51751814398234',
      32000: '51751814496538',
      50000: '51751814594842',
      64000: '51751814693146',
    },
  });

  const WHOLESALE_CUP_LID_VARIANT_MAPPING = Object.freeze({
    White: {
      10000: '50726870090010',
      16000: '50731610964250',
      32000: '50731610997018',
      64000: '50731611029786',
    },
    Black: {
      10000: '50726870057242',
      16000: '50731610898714',
      32000: '50731610931482',
      64000: '50731610931482',
    },
  });

  const WHOLESALE_RUSH_MULTIPLIERS = Object.freeze({
    wholesale_bowl: { standard: 1, '20days': 1.1, '10days': 1.35 },
    wholesale_cup: { standard: 1, '20days': 1.1, '10days': 1.2, '5days': 1.3 },
    wholesale_ice: { standard: 1, '20days': 1.1, '10days': 1.2, '5days': 1.3 },
  });

  const LID_VARIANT_MAPPING = Object.freeze({
    White: {
      250: '50675484590362',
      500: '50682897400090',
      1000: '50682897432858',
      2000: '50682897465626',
      3000: '50682897498394',
      4000: '50682897531162',
      5000: '50682897563930',
      10000: '50726870090010',
    },
    Black: {
      250: '50675484557594',
      500: '50682897203482',
      1000: '50682897236250',
      2000: '50682897269018',
      3000: '50682897301786',
      4000: '50682897334554',
      5000: '50682897367322',
      10000: '50726870057242',
    },
  });

  function getWholesaleLidQuantityTier(quantity) {
    const qty = parseInt(String(quantity).replace(/,/g, ''), 10) || 0;
    if (qty <= 10000) return '10000';
    if (qty <= 16000) return '16000';
    if (qty <= 32000) return '32000';
    if (qty <= 50000) return '50000';
    return '64000';
  }

  function getLidQuantityTier(cupQuantity) {
    const qty = parseInt(String(cupQuantity).replace(/,/g, ''), 10) || 0;
    if (qty <= 250) return '250';
    if (qty <= 500) return '500';
    if (qty <= 1000) return '1000';
    if (qty <= 2000) return '2000';
    if (qty <= 3000) return '3000';
    if (qty <= 4000) return '4000';
    if (qty <= 5000) return '5000';
    if (qty >= 10000) return '10000';
    return '5000';
  }

  function getProductionTimeLabel(days) {
    const dayCount = Number(days) || 10;
    if (dayCount === 10) return 'Standard (10 business days)';
    return `${dayCount} business day${dayCount === 1 ? '' : 's'}`;
  }

  function formatMoneyDollars(cents) {
    return `$${(cents / 100).toFixed(2)}`;
  }

  /** Match Shopify `| money` used in cart line properties (Base Order Total). */
  function formatBaseOrderTotal(cents) {
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents);
    }
    return formatMoneyDollars(cents);
  }

  function normalizeMoneyString(value) {
    if (value == null || value === '') return '';
    return String(value).replace(/[^\d.]/g, '');
  }

  function moneyPropertyMatches(propValue, cents) {
    if (!propValue || !cents) return false;
    const expected = formatBaseOrderTotal(cents);
    if (propValue === expected) return true;
    return normalizeMoneyString(propValue) === normalizeMoneyString(expected);
  }

  function assertCartResponse(data) {
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid cart response');
    }
    if (typeof data.status === 'number' && data.status >= 400) {
      throw new Error(data.description || data.message || 'Cart update failed');
    }
    if (data.errors) {
      const msg = typeof data.errors === 'string' ? data.errors : JSON.stringify(data.errors);
      throw new Error(msg);
    }
    return data;
  }

  function isRushLine(line) {
    const title = (line.product_title || line.title || '').toLowerCase();
    return title.includes('rush') || title.includes('rushed fee');
  }

  function isLidLine(line) {
    return line.properties && line.properties.Type === 'Lids';
  }

  function isPlateLine(line) {
    const title = (line.product_title || line.title || '').toLowerCase();
    return (line.properties && line.properties.Type === 'Printing Plates') || title.includes('printing plate');
  }

  function getBundleType(panel) {
    return panel.dataset.bundleType || 'cup';
  }

  function getTurnaroundCartLabel(turnaround, bundleType) {
    if (bundleType === 'wholesale_ice' || bundleType === 'wholesale_cup') {
      switch (turnaround) {
        case '20days':
          return 'Express (20 business days) +10%';
        case '10days':
          return 'Rushed (10 business days) +20%';
        case '5days':
          return 'Urgent (5 business days) +30%';
        default:
          return 'Standard (30 business days)';
      }
    }
    switch (turnaround) {
      case '20days':
        return 'Express (20 business days) +10%';
      case '10days':
        return 'Rushed (10 business days) +35%';
      default:
        return 'Standard (30 business days)';
    }
  }

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
  }

  function changeLineByKey(key, quantity, properties) {
    const body = { id: key, quantity };
    if (properties && typeof properties === 'object') {
      body.properties = properties;
    }
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    })
      .then((r) => r.json())
      .then(assertCartResponse);
  }

  /** Shopify /cart/change.js replaces all line properties — always merge with existing. */
  async function changeLineProperties(lineKey, quantity, propertyPatch) {
    const cart = await getCart();
    const line = cart.items.find((item) => item.key === lineKey);
    if (!line) {
      throw new Error('Cart line not found');
    }
    const merged = { ...(line.properties || {}), ...(propertyPatch || {}) };
    return changeLineByKey(lineKey, quantity ?? line.quantity, merged);
  }

  function defaultGroupForParent(parentKey) {
    return parentKey ? `epkorder-${parentKey}` : '';
  }

  async function resolveEpkGroup(panel) {
    const parentKey = panel.dataset.cupKey || '';
    let group = panel.dataset.epkGroup || '';
    const cart = await getCart();
    const parent = parentKey ? cart.items.find((item) => item.key === parentKey) : null;

    if (parent?.properties?._epk_group) {
      group = parent.properties._epk_group;
    } else if (!group && parentKey) {
      group = defaultGroupForParent(parentKey);
    }

    if (parent && group && parent.properties?._epk_group !== group) {
      await changeLineProperties(parentKey, parent.quantity, { _epk_group: group });
    }

    if (group) {
      panel.dataset.epkGroup = group;
    }
    return { group, cart, parent, parentKey };
  }

  function collectLinkedLines(cart, { group, parentKey, size, qty, bundleType }) {
    const parent = parentKey ? cart.items.find((item) => item.key === parentKey) : null;
    // Match the rush line by its FULL-price base (original_line_price) — same basis used when
    // the rush is added (getBundleBaseCents), so the "Base Order Total" property lines up.
    const baseCents = (parent?.original_line_price || parent?.final_line_price) ?? 0;
    const baseMoney = baseCents ? formatBaseOrderTotal(baseCents) : '';
    const bowlForProduct = size ? `${size} Bowl` : '';
    const bowlsLabel = size && qty ? `${size} ${qty} bowls` : '';
    const cupsLabel = size && qty ? `${size} ${qty} cups` : '';
    const linked = [];

    cart.items.forEach((line) => {
      if (line.key === parentKey) return;
      const props = line.properties || {};

      if (group && props._epk_group === group) {
        linked.push(line);
        return;
      }

      if (isRushLine(line) && baseCents > 0 && moneyPropertyMatches(props['Base Order Total'], baseCents)) {
        linked.push(line);
        return;
      }

      if (isLidLine(line)) {
        if (bundleType === 'wholesale_bowl' && bowlForProduct && props['For Product'] === bowlForProduct) {
          linked.push(line);
          return;
        }
        if (size && props['For Cup Size'] === size && (!qty || props.Quantity === String(qty))) {
          linked.push(line);
        }
        return;
      }

      if (isPlateLine(line)) {
        if (bowlsLabel && props['For Order'] === bowlsLabel) {
          linked.push(line);
        } else if (cupsLabel && props['For Order'] === cupsLabel) {
          linked.push(line);
        }
      }
    });

    return linked;
  }

  async function stampGroupOnBundle(panel) {
    const bundleType = getBundleType(panel);
    const size = panel.dataset.size || '';
    const qty = panel.dataset.quantity || '';
    const { group, cart, parentKey } = await resolveEpkGroup(panel);
    if (!group || !parentKey) return cart;

    const linked = collectLinkedLines(cart, { group, parentKey, size, qty, bundleType });
    for (const line of linked) {
      if (line.properties?._epk_group !== group) {
        await changeLineProperties(line.key, line.quantity, { _epk_group: group });
      }
    }

    return getCart();
  }

  async function finishBundleUpdate(panel) {
    try {
      await stampGroupOnBundle(panel);
      // In the drawer we patch the drawer section live. On the /cart PAGE the panel isn't
      // inside <cart-drawer>, so there's nothing to patch there — reload so the grouped
      // cards + estimated total reflect the change.
      if (panel.closest('cart-drawer')) {
        await refreshDrawer();
      } else {
        window.location.reload();
        return;
      }
    } finally {
      setBusy(panel, false, '');
    }
  }

  function addItems(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    }).then(async (r) => {
      const data = await r.json();
      if (!r.ok) {
        throw new Error(data?.description || data?.message || `Add failed (${r.status})`);
      }
      return assertCartResponse(data);
    });
  }

  function findRushLines(cart, group, baseCents) {
    return cart.items.filter((line) => {
      if (!isRushLine(line)) return false;
      const props = line.properties || {};
      if (group && props._epk_group === group) return true;
      if (baseCents > 0 && moneyPropertyMatches(props['Base Order Total'], baseCents)) return true;
      return false;
    });
  }

  function findRushLine(cart, group, baseCents) {
    return findRushLines(cart, group, baseCents)[0] || null;
  }

  async function removeRushLines(cart, group, baseCents) {
    const lines = findRushLines(cart, group, baseCents);
    for (const line of lines) {
      await changeLineByKey(line.key, 0);
    }
  }

  async function getBundleBaseCents(panel) {
    const { parent } = await resolveEpkGroup(panel);
    // Rush fee must be 40% of the FULL (pre-discount) cup price. Use original_line_price so the
    // base is never the already-discounted price — otherwise the member discount gets applied
    // twice to the rush (once in this calc, once by Shopify). Shopify then discounts the rush
    // line once, on its own.
    const fromCart = (typeof parent?.original_line_price === 'number' && parent.original_line_price > 0)
      ? parent.original_line_price
      : parent?.final_line_price;
    if (typeof fromCart === 'number' && fromCart > 0) return fromCart;
    return parseInt(panel.dataset.lineTotalCents || '0', 10) || 0;
  }

  function findLidLine(cart, group, size, qty, bundleType) {
    const bowlForProduct = size ? `${size} Bowl` : '';
    return cart.items.find((line) => {
      if (!isLidLine(line)) return false;
      const props = line.properties || {};
      if (group && props._epk_group === group) return true;
      if (bundleType === 'wholesale_bowl' && bowlForProduct && props['For Product'] === bowlForProduct) {
        return true;
      }
      if (size && props['For Cup Size'] === size && (!qty || props.Quantity === String(qty))) return true;
      return false;
    });
  }

  function findPlateLine(cart, group, size, qty) {
    const bowlsLabel = size && qty ? `${size} ${qty} bowls` : '';
    const cupsLabel = size && qty ? `${size} ${qty} cups` : '';
    return cart.items.find((line) => {
      if (!isPlateLine(line)) return false;
      const props = line.properties || {};
      if (group && props._epk_group === group) return true;
      if (bowlsLabel && props['For Order'] === bowlsLabel) return true;
      if (cupsLabel && props['For Order'] === cupsLabel) return true;
      return false;
    });
  }

  async function refreshDrawer() {
    const cart = await getCart();
    const updater =
      typeof window.updateCartDrawerFromSections === 'function'
        ? window.updateCartDrawerFromSections
        : null;

    if (updater) {
      await updater(cart);
      return cart;
    }

    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    return cart;
  }

  function setBusy(panel, busy, message) {
    panel.querySelectorAll('select').forEach((sel) => {
      sel.disabled = busy;
    });
    const messageEl = panel.querySelector('.epk-cart-cup-addons__message');
    if (messageEl) {
      if (message != null) {
        messageEl.textContent = message;
      } else if (!busy) {
        messageEl.textContent = '';
      }
    }
    panel.classList.toggle('epk-cart-cup-addons--busy', busy);
  }

  async function applyProduction(panel, days) {
    const feePercent = PRODUCTION_FEE_PERCENTAGES[days] ?? 0;

    setBusy(panel, true, 'Updating…');

    try {
      const { group } = await resolveEpkGroup(panel);
      const lineTotalCents = await getBundleBaseCents(panel);
      const baseMoney = formatBaseOrderTotal(lineTotalCents);
      const cart = await getCart();

      await removeRushLines(cart, group, lineTotalCents);

      if (feePercent > 0 && lineTotalCents > 0) {
        const rushFeeAmount = (lineTotalCents / 100) * (feePercent / 100);
        const rushFeeQuantity = Math.max(1, Math.round(rushFeeAmount));
        const productionLabel = getProductionTimeLabel(days);
        await addItems([
          {
            id: RUSH_VARIANT_ID,
            quantity: rushFeeQuantity,
            properties: {
              'Base Order Total': baseMoney,
              'Production Time': productionLabel,
              [`Production Fee (${productionLabel}) +${feePercent}%`]: formatBaseOrderTotal(
                Math.round(rushFeeAmount * 100)
              ),
              _epk_group: group,
            },
          },
        ]);
      }

      await finishBundleUpdate(panel);
    } catch (error) {
      console.error('[epk-cup-cart-addons] production', error);
      setBusy(panel, false, 'Could not update. Please try again.');
    }
  }

  async function applyWholesaleTurnaround(panel, turnaround) {
    const bundleType = getBundleType(panel);
    const multipliers = WHOLESALE_RUSH_MULTIPLIERS[bundleType] || WHOLESALE_RUSH_MULTIPLIERS.wholesale_bowl;
    const multiplier = multipliers[turnaround] ?? 1;
    const turnaroundLabel = getTurnaroundCartLabel(turnaround, bundleType);

    setBusy(panel, true, 'Updating…');

    try {
      const { group, parentKey, parent } = await resolveEpkGroup(panel);
      const lineTotalCents = await getBundleBaseCents(panel);
      const baseMoney = formatBaseOrderTotal(lineTotalCents);

      if (parentKey && parent) {
        await changeLineProperties(parentKey, parent.quantity, { Turnaround: turnaroundLabel });
      }

      const cartAfterParent = await getCart();
      await removeRushLines(cartAfterParent, group, lineTotalCents);

      if (multiplier > 1 && lineTotalCents > 0) {
        const rushFeeAmount = (lineTotalCents / 100) * (multiplier - 1);
        const rushFeeQuantity = Math.max(1, Math.round(rushFeeAmount));
        await addItems([
          {
            id: RUSH_VARIANT_ID,
            quantity: rushFeeQuantity,
            properties: {
              'Base Order Total': baseMoney,
              Turnaround: turnaroundLabel,
              [`Rush Fee (${turnaround})`]: formatBaseOrderTotal(Math.round(rushFeeAmount * 100)),
              _epk_group: group,
            },
          },
        ]);
      }

      await finishBundleUpdate(panel);
    } catch (error) {
      console.error('[epk-cup-cart-addons] wholesale turnaround', error);
      setBusy(panel, false, 'Could not update. Please try again.');
    }
  }

  async function applyLids(panel, choice) {
    const size = panel.dataset.size || '';
    const qty = panel.dataset.quantity || '';
    const bundleType = getBundleType(panel);

    setBusy(panel, true, 'Updating…');

    try {
      const { group, cart } = await resolveEpkGroup(panel);
      const existingLid = findLidLine(cart, group, size, qty, bundleType);

      if (existingLid) {
        await changeLineByKey(existingLid.key, 0);
      }

      if (choice && choice !== 'no') {
        const isBowl = bundleType === 'wholesale_bowl';
        const isWholesaleCup = bundleType === 'wholesale_cup';
        const lidTier =
          isBowl || isWholesaleCup ? getWholesaleLidQuantityTier(qty) : getLidQuantityTier(qty);
        const mapping = isBowl
          ? BOWL_LID_VARIANT_MAPPING
          : isWholesaleCup
            ? WHOLESALE_CUP_LID_VARIANT_MAPPING
            : LID_VARIANT_MAPPING;
        const lidVariantId = mapping[choice]?.[lidTier];
        if (!lidVariantId) {
          setBusy(panel, false, 'Lids are not available for this quantity.');
          return;
        }
        const lidProperties = isBowl
          ? {
              Type: 'Lids',
              Style: choice,
              'For Product': `${size} Bowl`,
              _epk_group: group,
            }
          : {
              Type: 'Lids',
              Color: choice,
              Quantity: String(qty),
              'For Cup Size': size,
              'For Cup Order': `${qty} cups`,
              _epk_group: group,
            };
        await addItems([
          {
            id: lidVariantId,
            quantity: 1,
            properties: lidProperties,
          },
        ]);
      }

      await finishBundleUpdate(panel);
    } catch (error) {
      console.error('[epk-cup-cart-addons] lids', error);
      setBusy(panel, false, 'Could not update. Please try again.');
    }
  }

  function bindPanel(panel) {
    if (panel.dataset.epkCupAddonsBound === 'true') return;
    panel.dataset.epkCupAddonsBound = 'true';

    // Keep changes from bubbling into the cart line quantity handler.
    panel.addEventListener('click', (event) => event.stopPropagation());

    const productionSelect = panel.querySelector('.epk-cart-cup-addons__production');
    const turnaroundSelect = panel.querySelector('.epk-cart-cup-addons__turnaround');
    const lidsSelect = panel.querySelector('.epk-cart-cup-addons__lids');

    productionSelect?.addEventListener('change', (event) => {
      event.stopPropagation();
      applyProduction(panel, parseInt(productionSelect.value, 10));
    });

    turnaroundSelect?.addEventListener('change', (event) => {
      event.stopPropagation();
      applyWholesaleTurnaround(panel, turnaroundSelect.value);
    });

    lidsSelect?.addEventListener('change', (event) => {
      event.stopPropagation();
      applyLids(panel, lidsSelect.value);
    });
  }

  function initCupAddons(root = document) {
    root.querySelectorAll('[data-epk-cup-addons]').forEach(bindPanel);
  }

  // Remove a whole cup group (cup + linked lids + rush) from one trash click.
  async function handleGroupRemove(removeButton) {
    const group = removeButton.dataset.epkGroup || '';
    const size = removeButton.dataset.epkSize || '';
    const qty = removeButton.dataset.epkQty || '';
    const index = parseInt(removeButton.dataset.index || '0', 10);

    try {
      const cart = await getCart();
      const cupLine = index > 0 ? cart.items[index - 1] : null;
      const baseCents = cupLine?.final_line_price ?? 0;

      const updates = {};
      if (cupLine) updates[cupLine.key] = 0;

      const rushLines = findRushLines(cart, group, baseCents);
      rushLines.forEach((rush) => {
        updates[rush.key] = 0;
      });

      const bundleType = removeButton.dataset.epkBundleType || 'cup';
      const lid = findLidLine(cart, group, size, qty, bundleType);
      if (lid) updates[lid.key] = 0;

      const plate = findPlateLine(cart, group, size, qty);
      if (plate) updates[plate.key] = 0;

      if (Object.keys(updates).length === 0) return false;

      const finalCart = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      }).then((r) => r.json());

      await refreshDrawer(finalCart);
      return true;
    } catch (error) {
      console.error('[epk-cup-cart-addons] group remove', error);
      return false;
    }
  }

  // Capture-phase so we run before the default cart-remove-button handler.
  document.addEventListener(
    'click',
    (event) => {
      const removeButton = event.target.closest?.('cart-remove-button[data-epk-group]');
      if (!removeButton) return;
      event.stopImmediatePropagation();
      event.preventDefault();
      handleGroupRemove(removeButton);
    },
    true
  );

  window.initCupAddons = initCupAddons;

  document.addEventListener('DOMContentLoaded', () => initCupAddons());
  document.addEventListener('cart-drawer:open', () => {
    requestAnimationFrame(() => initCupAddons());
  });
  document.addEventListener('cart:updated', () => {
    requestAnimationFrame(() => initCupAddons());
  });
})();
